import { type FC, type PropsWithChildren } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { IconTouch } from '@redteclab/icons'

import { Link, LoadingButton, Notification } from 'base-ui'

import { FriendlyCaptchaChallenge } from '../../../friendly-captcha/components/FriendlyCaptchaChallenge/FriendlyCaptchaChallenge'
import { LoginRegistrationUserLoginGeneralError } from '../../../login-registration/components/LoginRegistrationUserLoginForm/LoginRegistrationUserLoginGeneralError'

import { LoginRegistrationUserPasswordField } from './LoginRegistrationUserPasswordField'
import { type PasswordLoginSubmissionResult } from './useLoginRegistrationUserPasswordLoginSubmission'

type LoginRegistrationUserPasswordFormProps = {
  captchaSolution: string
  forgotPasswordUrl: string
  onCaptchaDone: (fcSolution: string) => void
  onChangeEmail: () => void
  submission: PasswordLoginSubmissionResult
}

export const LoginRegistrationUserPasswordForm: FC<
  PropsWithChildren<LoginRegistrationUserPasswordFormProps>
> = ({
  captchaSolution,
  children,
  forgotPasswordUrl,
  onCaptchaDone,
  onChangeEmail,
  submission,
}) => {
  const intl = useIntl()
  const {
    isLoginMutating,
    isOtpMutating,
    isSubmitting,
    loginError,
    onFormSubmit,
    otpError,
    requestOtp,
    submitIntent,
  } = submission
  const isFriendlyCaptchaDone = Boolean(captchaSolution)
  const onRequestOtp = (): void => {
    void requestOtp(captchaSolution)
  }

  return (
    <form data-qa-id="loginModal" onSubmit={onFormSubmit}>
      {children}
      {loginError ? (
        <LoginRegistrationUserLoginGeneralError
          className="mb-3.5"
          error={loginError}
        />
      ) : null}
      {otpError ? (
        <Notification className="mb-3.5" severity="error">
          {intl.formatMessage({ id: 'general.error' })}
        </Notification>
      ) : null}
      <LoginRegistrationUserPasswordField />
      <LoadingButton
        className="mt-4"
        data-qa-id="login-form-otp-request"
        disabled={!isFriendlyCaptchaDone || isSubmitting}
        loading={submitIntent === 'otp' || isOtpMutating}
        loadingPosition="start"
        onClick={onRequestOtp}
        size="large"
        startIcon={<IconTouch />}
        type="button"
        variant="text"
      >
        <FormattedMessage id="login.otp.requestLoginCode" />
      </LoadingButton>
      <FriendlyCaptchaChallenge
        className="mb-2"
        doneCallback={onCaptchaDone}
        startMode="auto"
        whenThisPropChangesResetCaptcha={loginError ?? otpError}
      />
      <LoadingButton
        className="w-full"
        data-qa-id="login-form-loginForm-submit-button"
        disabled={!isFriendlyCaptchaDone || isSubmitting}
        loading={submitIntent === 'password' || isLoginMutating}
        loadingPosition="end"
        type="submit"
      >
        <FormattedMessage id="general.label.subscribe" />
      </LoadingButton>
      <div className="mt-5 text-center">
        <button
          className="cursor-pointer border-none bg-transparent p-0 text-dark-brand underline hover:text-dark-primary-max"
          data-qa-id="login-form-change-email"
          disabled={isSubmitting}
          onClick={onChangeEmail}
          type="button"
        >
          <FormattedMessage id="login.otp.changeEmail" />
        </button>
      </div>
      <div className="mt-5 text-center">
        <Link
          className="font-medium"
          data-qa-id="login-form-forgot-password-link"
          href={forgotPasswordUrl}
        >
          <FormattedMessage id="general.link.label.password.forgotten" />
        </Link>
      </div>
    </form>
  )
}
